import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { BrandMark } from "../BrandMark";

interface LegalLayoutProps {
  title: string;
  /** German "Stand: Mai 2026" or "Angaben gemäß § 5 TMG" subtitle. */
  subtitle?: string;
  children: ReactNode;
}

/**
 * Public-route layout for `/impressum` + `/datenschutz`. No auth gate — by §5
 * TMG these pages must be reachable in ≤ 2 clicks from anywhere on the site,
 * including the unauthenticated login page. Renders the brand mark on top
 * and a small footer with copyright + cross-link to the other legal page.
 */
export function LegalLayout({ title, subtitle, children }: LegalLayoutProps) {
  return (
    <main className="min-h-full">
      <div className="mx-auto w-full max-w-2xl px-4 pt-10 pb-8 sm:px-6 sm:pt-12 safe-top">
        <div className="flex items-center justify-between mb-6 gap-3">
          <Link
            to="/"
            aria-label="PPLearning — Startseite"
            className="inline-flex items-center focus:outline-none focus-visible:ring-2 focus-visible:ring-outline-strong rounded-lg"
          >
            <BrandMark variant="horizontal" className="h-7 w-auto" />
          </Link>
          <Link
            to="/"
            className="min-h-[44px] inline-flex items-center px-3 py-2 rounded-lg text-sm text-fg-muted hover:text-fg hover:bg-card focus:outline-none focus-visible:ring-2 focus-visible:ring-outline-strong"
          >
            ← Zurück
          </Link>
        </div>

        <header className="space-y-1 mb-6">
          <h1 className="text-2xl font-bold tracking-tight text-fg">{title}</h1>
          {subtitle && (
            <p className="text-xs uppercase tracking-widest text-fg-faint">{subtitle}</p>
          )}
        </header>

        <article className="bg-card border border-outline rounded-2xl p-5 sm:p-6 space-y-6 text-fg-soft text-sm leading-relaxed">
          {children}
        </article>

        <footer className="mt-8 flex flex-col items-center gap-2 text-center">
          <div className="text-[10px] uppercase tracking-widest text-fg-faint">
            PPLearning · English Trainer for Private Pilot Licence
          </div>
          <div className="flex gap-3 text-xs text-accent">
            <Link to="/impressum" className="hover:underline">Impressum</Link>
            <span className="text-fg-faintest" aria-hidden>·</span>
            <Link to="/datenschutz" className="hover:underline">Datenschutz</Link>
          </div>
          <div className="text-[10px] text-fg-faint tracking-wide">
            © 2026 Concept4IT Consulting GmbH
          </div>
        </footer>
      </div>
    </main>
  );
}
